// ─────────────────────────────────────────────────────────────────────────────
// Lembretes de pausa — quando cutucar quem esqueceu o status marcado.
//
// Este módulo é só regra: não conhece React, rede nem relógio próprio. Recebe o
// tipo da pausa e há quantos minutos ela está aberta, e responde quantos
// lembretes já eram devidos. Por isso roda inteiro sob `node`, em
// scripts/teste-lembrete-pausa.mjs.
//
// ─────────────────────────────────────────────────────────────────────────────
// POR QUE CONTAR LEMBRETES DEVIDOS, E NÃO "ESTÁ NA HORA?"
//
// O jeito óbvio seria perguntar a cada minuto "o relógio bateu 60?". Foi assim
// que os lembretes de horário do expediente foram escritos, e é por isso que
// eles nunca chegavam no navegador: com a aba em segundo plano o Chrome agrupa
// os temporizadores, o tique pula de 11:59 para 12:02, a janela de disparo
// passa no meio — e o aviso se perde para sempre. No app isso não acontece
// porque o Electron desliga o freio (`backgroundThrottling: false`, em
// electron/main.js); no navegador não há esse botão.
//
// Aqui a pergunta é outra: "desde que a pausa começou, quantos lembretes já
// deveriam ter saído?". É uma conta sobre o tempo decorrido, não sobre o
// instante do tique. A aba pode congelar quarenta minutos: quando ela acordar,
// a conta acusa a diferença e o lembrete sai. Atrasado, mas sai.
// ─────────────────────────────────────────────────────────────────────────────

/** Almoço: o operador só é lembrado depois de uma hora fora. */
export const ESPERA_ALMOCO_MIN = 60;

/** Demais pausas: o lembrete começa aos 15 minutos. */
export const ESPERA_PAUSA_MIN = 15;

/** Passado o primeiro lembrete, todos se repetem neste intervalo. */
export const INTERVALO_REAVISO_MIN = 15;

/**
 * Quanto tempo cada pausa corre antes do PRIMEIRO lembrete.
 *
 * Ficam de fora, de propósito:
 *   · `fora_expediente` — o expediente acabou, não há a que voltar;
 *   · `ociosidade`      — não foi declarada, foi apurada, e já tem o reaviso
 *                         próprio do monitor (ver services/useAtividade.js).
 */
const ESPERA_POR_TIPO = {
  almoco: ESPERA_ALMOCO_MIN,
  cafe: ESPERA_PAUSA_MIN,
  apoio: ESPERA_PAUSA_MIN,
  outro: ESPERA_PAUSA_MIN,
};

/** Minutos até o primeiro lembrete deste tipo; `null` quando ele não lembra. */
export function esperaInicialMin(tipo) {
  const espera = ESPERA_POR_TIPO[tipo];
  return espera == null ? null : espera;
}

/** True quando a pausa deste tipo gera lembrete. */
export function pausaLembra(tipo) {
  return esperaInicialMin(tipo) != null;
}

/**
 * Quantos lembretes já eram devidos, dada a duração da pausa.
 *
 * Almoço (espera 60, intervalo 15): 59 min → 0 · 60 → 1 · 74 → 1 · 75 → 2.
 * Café   (espera 15, intervalo 15): 14 min → 0 · 15 → 1 · 30 → 2.
 *
 * O chamador guarda quantos já entregou; a diferença é o que falta avisar. Um
 * número maior que o guardado significa que o tempo passou sem ninguém ver — a
 * aba dormiu, a máquina suspendeu — e o lembrete sai assim mesmo.
 */
export function lembretesDevidos(tipo, minutosDePausa) {
  const espera = esperaInicialMin(tipo);
  if (espera == null) return 0;
  if (!Number.isFinite(minutosDePausa) || minutosDePausa < espera) return 0;
  return 1 + Math.floor((minutosDePausa - espera) / INTERVALO_REAVISO_MIN);
}

/** Minutos decorridos desde o início da pausa. `null` sem início conhecido. */
export function minutosNaPausa(inicio, agora = new Date()) {
  if (!inicio) return null;
  const t = inicio instanceof Date ? inicio.getTime() : new Date(inicio).getTime();
  if (!Number.isFinite(t)) return null;
  const min = (agora.getTime() - t) / 60000;
  return min < 0 ? 0 : Math.floor(min);
}

/** "1h20" · "45 min" — a duração como o operador a leria em voz alta. */
export function formatarDuracao(min) {
  const m = Math.max(0, Math.round(min || 0));
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const resto = m % 60;
  return resto ? `${h}h${String(resto).padStart(2, '0')}` : `${h}h`;
}

/**
 * O texto do lembrete.
 *
 * Diz o tempo REAL da pausa, e não "passou do limite": o número é o que faz o
 * operador decidir. "Seu almoço já dura 1h30" pesa; "você excedeu" não diz
 * quanto.
 */
export function textoDoLembrete(tipo, minutos, rotulo = 'Pausa') {
  const duracao = formatarDuracao(minutos);

  if (tipo === 'almoco') {
    return {
      titulo: 'Almoço em aberto',
      corpo: `Seu almoço já dura ${duracao}. Volte o status para Ativo — o tempo continua correndo até você desmarcar.`,
    };
  }

  return {
    titulo: `${rotulo} em aberto`,
    corpo: `Sua pausa (${rotulo.toLowerCase()}) já dura ${duracao}. Volte o status para Ativo quando retomar o atendimento.`,
  };
}
